import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useLanguage } from '../context/LanguageContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import { toast } from 'sonner'
import { ArrowLeft, Bell, Save, RefreshCw } from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

export default function InvestmentReminderPage() {
  const navigate = useNavigate()
  const { locale } = useLanguage()
  const isSpanish = locale.startsWith('es')
  
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/investments/reminder-settings`)
      setSettings(response.data)
    } catch (error) {
      console.error('Error fetching settings:', error)
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])
  
  const handleSave = async () => {
    setSaving(true)
    try {
      await axios.put(`${API}/api/investments/reminder-settings`, {
        enabled: settings.enabled,
        reminder_day_of_month: settings.reminder_day_of_month,
        reminder_time: settings.reminder_time,
        timezone: settings.timezone,
        mensaje: settings.mensaje
      })
      toast.success(isSpanish ? 'Configuración guardada' : 'Settings saved')
    } catch (error) {
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error al guardar' : 'Error saving'))
    } finally {
      setSaving(false)
    }
  }
  
  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }
  
  // Day options (1-28)
  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1)
  
  // Time options (every hour)
  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = String(i).padStart(2, '0')
    return `${hour}:00`
  })
  
  if (loading) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">
          {isSpanish ? 'Cargando...' : 'Loading...'}
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-4 md:p-8 pb-safe max-w-xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/inversiones')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">
            {isSpanish ? 'Recordatorio Mensual' : 'Monthly Reminder'}
          </h1>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">
                {isSpanish ? 'Notificación de Inversiones' : 'Investment Notification'}
              </CardTitle>
              <CardDescription>
                {isSpanish 
                  ? 'Te enviaremos un recordatorio cada mes para registrar los rendimientos del nuevo mes.'
                  : 'We\'ll send you a reminder each month to record the new month\'s returns.'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">{isSpanish ? 'Activar recordatorio' : 'Enable reminder'}</Label>
              <p className="text-sm text-muted-foreground">
                {isSpanish ? 'Recibir notificación mensual' : 'Receive monthly notification'}
              </p>
            </div>
            <Switch
              checked={settings?.enabled || false}
              onCheckedChange={(checked) => updateField('enabled', checked)}
              data-testid="reminder-toggle"
            />
          </div>
          
          {settings?.enabled && (
            <>
              {/* Day of Month */}
              <div className="space-y-2">
                <Label>{isSpanish ? 'Día del mes' : 'Day of month'}</Label>
                <Select 
                  value={String(settings?.reminder_day_of_month || 1)}
                  onValueChange={(v) => updateField('reminder_day_of_month', parseInt(v))}
                >
                  <SelectTrigger data-testid="reminder-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOptions.map(day => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {isSpanish 
                    ? 'Máximo día 28 para funcionar en todos los meses'
                    : 'Max day 28 to work in all months'}
                </p>
              </div>
              
              {/* Time */}
              <div className="space-y-2">
                <Label>{isSpanish ? 'Hora' : 'Time'}</Label>
                <Select 
                  value={settings?.reminder_time || '09:00'}
                  onValueChange={(v) => updateField('reminder_time', v)}
                >
                  <SelectTrigger data-testid="reminder-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map(time => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Timezone */}
              <div className="space-y-2">
                <Label>{isSpanish ? 'Zona horaria' : 'Timezone'}</Label>
                <Select 
                  value={settings?.timezone || 'America/Mexico_City'}
                  onValueChange={(v) => updateField('timezone', v)}
                >
                  <SelectTrigger data-testid="reminder-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/Mexico_City">America/Mexico_City</SelectItem>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                    <SelectItem value="Europe/Madrid">Europe/Madrid</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Message */}
              <div className="space-y-2">
                <Label>{isSpanish ? 'Mensaje personalizado' : 'Custom message'}</Label>
                <Textarea
                  value={settings?.mensaje || ''}
                  onChange={(e) => updateField('mensaje', e.target.value)}
                  placeholder={
                    isSpanish 
                      ? 'Nuevo mes en KaizenOS: registra tus rendimientos de inversión.'
                      : 'New month in KaizenOS: record your investment returns.'
                  }
                  rows={3}
                  data-testid="reminder-message-input"
                />
              </div>
            </>
          )}
          
          {/* Save Button */}
          <Button 
            className="w-full"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-reminder-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isSpanish ? 'Guardar configuración' : 'Save settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
